import apiClient from '../core';
import type {
  Blog,
  BlogPost,
  CreateBlogRequest,
  UpdateBlogRequest,
  CreateBlogPostRequest,
  UpdateBlogPostRequest,
  BlogStats,
  BlogPostStatus,
} from '../../schemas/validation';

export const blogApi = {
  // Install blog app on a site
  async install(
    siteId: string,
    data?: CreateBlogRequest
  ): Promise<{ message: string; blog: Blog }> {
    const response = await apiClient.post<{ message: string; blog: Blog }>(
      `/sites/${siteId}/apps/blog`,
      data || {}
    );
    return response.data;
  },

  // Get blog configuration and stats
  async get(siteId: string): Promise<{ blog: Blog; stats: BlogStats }> {
    const response = await apiClient.get<{ blog: Blog; stats: BlogStats }>(
      `/sites/${siteId}/apps/blog`
    );
    return response.data;
  },

  // Update blog settings
  async update(siteId: string, data: UpdateBlogRequest): Promise<{ message: string; blog: Blog }> {
    const response = await apiClient.put<{ message: string; blog: Blog }>(
      `/sites/${siteId}/apps/blog`,
      data
    );
    return response.data;
  },

  // Uninstall blog app (deletes all blog data)
  async uninstall(siteId: string): Promise<{ message: string }> {
    const response = await apiClient.delete<{ message: string }>(`/sites/${siteId}/apps/blog`);
    return response.data;
  },

  // Reset blog (delete all posts and tags, keep settings)
  async reset(siteId: string): Promise<{ message: string }> {
    const response = await apiClient.post<{ message: string }>(`/sites/${siteId}/apps/blog/reset`);
    return response.data;
  },

  // Publish or unpublish blog
  async setPublished(
    siteId: string,
    isPublished: boolean
  ): Promise<{ message: string; blog: Blog }> {
    const response = await apiClient.put<{ message: string; blog: Blog }>(
      `/sites/${siteId}/apps/blog/publish`,
      { isPublished }
    );
    return response.data;
  },

  // Get all tags used in blog
  async getTags(siteId: string): Promise<{ tags: Array<{ tag: string; count: number }> }> {
    const response = await apiClient.get<{
      tags: Array<{ tag: string; count: number }>;
    }>(`/sites/${siteId}/apps/blog/tags`);
    return response.data;
  },

  // Blog Posts
  posts: {
    // List posts with pagination and filtering
    async list(
      siteId: string,
      params?: {
        page?: number;
        limit?: number;
        status?: BlogPostStatus;
        tag?: string;
      }
    ): Promise<{
      posts: BlogPost[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    }> {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.status) queryParams.append('status', params.status);
      if (params?.tag) queryParams.append('tag', params.tag);

      const url = queryParams.toString()
        ? `/sites/${siteId}/apps/blog/posts?${queryParams.toString()}`
        : `/sites/${siteId}/apps/blog/posts`;

      const response = await apiClient.get<{
        posts: BlogPost[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
        };
      }>(url);
      return response.data;
    },

    // Create a new post
    async create(
      siteId: string,
      data: CreateBlogPostRequest
    ): Promise<{ message: string; post: BlogPost }> {
      const response = await apiClient.post<{
        message: string;
        post: BlogPost;
      }>(`/sites/${siteId}/apps/blog/posts`, data);
      return response.data;
    },

    // Get a single post
    async get(siteId: string, postId: string): Promise<{ post: BlogPost }> {
      const response = await apiClient.get<{ post: BlogPost }>(
        `/sites/${siteId}/apps/blog/posts/${postId}`
      );
      return response.data;
    },

    // Update a post
    async update(
      siteId: string,
      postId: string,
      data: UpdateBlogPostRequest
    ): Promise<{ message: string; post: BlogPost }> {
      const response = await apiClient.put<{ message: string; post: BlogPost }>(
        `/sites/${siteId}/apps/blog/posts/${postId}`,
        data
      );
      return response.data;
    },

    // Delete a post
    async delete(siteId: string, postId: string): Promise<{ message: string }> {
      const response = await apiClient.delete<{ message: string }>(
        `/sites/${siteId}/apps/blog/posts/${postId}`
      );
      return response.data;
    },

    // Publish or unpublish a post
    async setPublished(
      siteId: string,
      postId: string,
      status: BlogPostStatus
    ): Promise<{ message: string; post: BlogPost }> {
      const response = await apiClient.put<{ message: string; post: BlogPost }>(
        `/sites/${siteId}/apps/blog/posts/${postId}/publish`,
        { status }
      );
      return response.data;
    },
  },
};
